import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BILLING_SETTINGS_PATH } from "../../../routes/paths";
import { PRODUCT_NAME } from "../../billing/data/subscriptionPlans";
import bannerArtwork from "../../../assets/svg/Dsahboard/banner.png";

export interface UpgradeBannerProps {
  /**
   * Whether the fortnight is what has just run out, which is what decides the
   * wording — the same fact `useSubscriptionGate` names `trialEnded`, and the
   * same one the locked create buttons on this screen are answering with.
   *
   * The strip is drawn either way: both readers have no plan, and both are
   * being offered the same one. What changes is only whether they are being
   * asked to keep something they still have, or told they have lost it.
   */
  trialEnded?: boolean;
}

/**
 * The strip across the top of the dashboard: what the workspace is not on yet,
 * and the way onto it.
 *
 * Only ever drawn for a workspace that has no plan — see `DashboardPage`. An
 * offer to upgrade is a claim about somebody's account, so it is not put to
 * someone already paying, and not put while the billing read has yet to answer.
 * Same rule as the rail's "Subscribe now" line.
 *
 * Two ways of saying it, on either side of the trial. "Keep going with Pro" is
 * an offer to somebody who still has the product open — a workspace on its
 * fortnight, or one that never took a trial at all. Past the end date it would
 * be describing something that is no longer true, so the strip says plainly
 * what happened and what buying gets back. Same reasoning as the locked
 * buttons' two answers in `gateReason`.
 *
 * The artwork is a background rather than an `<img>`: it carries no information
 * the two lines over it do not already say, and as a background it can be
 * cropped to whatever width the screen gives without the words moving with it.
 */
const UpgradeBanner: React.FC<UpgradeBannerProps> = ({ trialEnded = false }) => (
  <section
    // 4700x600 — the same proportions as the block below, so `cover` crops from
    // the sides on a narrow screen rather than eating the swirl.
    // The blue behind the artwork, sampled from it — what the words sit on for
    // the moment before the image is decoded, and if it never loads at all.
    style={{
      backgroundImage: `url(${bannerArtwork})`,
      backgroundColor: "#3451D9",
    }}
    className="mt-5 overflow-hidden rounded-lg bg-cover bg-center flex flex-wrap items-center justify-between gap-4 min-h-[120px] sm:min-h-[140px] 3xl:min-h-[170px] py-5 pl-6 pr-6 sm:pl-10 sm:pr-8 lg:pl-24 xl:pl-36 3xl:pl-48"
  >
    <div>
      {/* Two lines of one sentence, so they are one heading with a break in it
          rather than a heading and a subtitle — the second line is the half
          that means something. */}
      <h2 className="text-[26px] 3xl:text-[28px] font-semibold  ">
        {/* The green the design puts on the product's name, and the only place
            in the app it is used — it exists to lift the name off the blue. */}
        <span className="block text-[#52FF89]">
          {trialEnded ? "Your Pro trial has ended" : "Keep going with Pro "}
        </span>
        <span className="block text-white">
          {trialEnded
            ? "Upgrade to keep Pro features."
            : "Don’t miss our exclusive launch offer!"}
        </span>
      </h2>
    </div>

    {/* A link rather than a button that navigates, for the reason the setup
        checklist gives: it is a screen of the app, so a middle click opens it
        in a tab and the status bar says where it leads. */}
    <Link
      to={BILLING_SETTINGS_PATH}
      // Solid white, with the label in the blue it is standing on — the one
      // thing on the strip that is pressed, so it is the one thing filled in.
      className="inline-flex flex-shrink-0 items-center gap-2 rounded-md bg-white px-4 py-2.5 3xl:px-5 3xl:py-3 text-[14px] 3xl:text-[16px] font-medium text-[#3F51A5] transition-colors hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#3451D9]"
    >
      {trialEnded ? "Upgrade to Pro" : "Claim launch offer"}
      <ArrowRight aria-hidden className="w-4 h-4" />
    </Link>
  </section>
);

export default UpgradeBanner;
